import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  NgHeaderTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { forkJoin, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { BranchInfo, CrewMemberResponse, ForemanDetailInfo, ForemanStatus } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';
import { CalendarService } from '../../services/calendar.service';
import { CrewScheduleStore } from '../../services/crew-schedule-store.service';
import { ProjectService, ProjectTypeMapping } from '../../services/project.service';
import { ConfigService } from '../../config.service';
import { extractHttpErrorMessage } from '../../utils/http-error';
import { CrewBonusGridComponent } from './crew-bonus-grid.component';
import { CrewBonusStore } from './crew-bonus-store.service';
import { ForemanTravelLinesComponent } from './foreman-travel-lines.component';
import { ReloadAfterSaveError } from './reload-after-save.error';
import { TravelAssignmentsStore } from './travel-assignments-store.service';

@Component({
  selector: 'app-foreman-configuration-modal',
  templateUrl: './foreman-configuration-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TravelAssignmentsStore, CrewBonusStore],
  imports: [
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    StateMessageComponent,
    CurrencyPipe,
    NgSelectComponent,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
    NgHeaderTemplateDirective,
    ReactiveFormsModule,
    FormsModule,
    ForemanTravelLinesComponent,
    CrewBonusGridComponent,
  ],
  styles: [
    `
      :host ::ng-deep .modal-title {
        font-weight: normal;
      }
      .foreman-config-header {
        background-color: #bfe0eb;
        border: 1px solid #72b1c8;
      }
      .foreman-config-header-value {
        font-size: 1.1rem;
      }
      .foreman-config-header-label {
        color: #636569;
        font-size: 0.8rem;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ForemanConfigurationModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly calendarService = inject(CalendarService);
  private readonly projectService = inject(ProjectService);
  private readonly branchService = inject(BranchService);
  private readonly store = inject(CrewScheduleStore);
  private readonly travelStore = inject(TravelAssignmentsStore);
  private readonly crewBonusStore = inject(CrewBonusStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly configService = inject(ConfigService);

  foremanUserId = signal('');
  detail = signal<ForemanDetailInfo | undefined>(undefined);
  pendingProjectTypeIds = signal<string[]>([]);
  isSaving = signal(false);
  saveError = signal(false);
  saveErrorMessage = signal('');
  isLoading = signal(false);
  hasError = signal(false);

  readonly ForemanStatus = ForemanStatus;
  projectTypes: ProjectTypeMapping[] = [];
  projectTypeFormControl = new FormControl<string[]>([]);
  allSelected = signal(false);
  projectTypeSearchTerm = signal('');
  availableDays = signal<number[]>([]);
  allBranches = signal<BranchInfo[]>([]);
  preferredCrewMembers = signal<CrewMemberResponse[]>([]);
  preferredCrewOptions = signal<CrewMemberResponse[]>([]);

  availableBranches = computed(() => {
    const homeBranchId = this.detail()?.homeBranchId;
    return this.allBranches().filter((b) => b.id !== homeBranchId);
  });

  readonly preferredCrewUserIds = computed(() => this.preferredCrewMembers().map((m) => m.userId));

  readonly days = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' },
  ];

  footerActions: ModalFooterAction[] = [
    {
      label: 'Save Changes',
      type: 'primary',
      action: () => this.onSaveChanges(),
      loading: () => this.isSaving(),
    },
  ];

  retryButton = {
    text: 'Try Again',
    onClick: () => this.loadData(this.foremanUserId()),
  };

  constructor() {
    this.travelStore.configure({
      onError: (msg) => {
        this.saveErrorMessage.set(msg);
        this.saveError.set(true);
        this.cdr.markForCheck();
      },
    });

    this.projectTypeFormControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((selectedIds) => {
      if (!selectedIds || selectedIds.length === 0) {
        this.pendingProjectTypeIds.set([]);
        this.allSelected.set(false);
        return;
      }
      this.pendingProjectTypeIds.set(selectedIds);
      this.allSelected.set(selectedIds.length === this.projectTypes.length);
    });

    effect(() => {
      const userId = this.foremanUserId();
      if (userId) this.loadData(userId);
    });
  }

  private loadData(foremanUserId: string): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.saveError.set(false);
    this.detail.set(undefined);
    this.pendingProjectTypeIds.set([]);

    forkJoin({
      detail: this.calendarService.getForemanDetail(foremanUserId),
      projectTypes: this.projectService.getProjectTypes(),
      availability: this.calendarService.getDefaultForemanAvailability(foremanUserId),
      travelAssignments: this.calendarService.getTravelAssignments(foremanUserId),
      branches: this.branchService.getBranches(),
      preferredCrew: this.calendarService.getPreferredCrewOptions(foremanUserId),
      assignedCrew: this.calendarService.getAssignedPreferredCrew(foremanUserId),
      crewBonusPercentages: this.calendarService.getCrewBonusPercentages(foremanUserId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({
          detail,
          projectTypes,
          availability,
          travelAssignments,
          branches,
          preferredCrew,
          assignedCrew,
          crewBonusPercentages,
        }) => {
          this.detail.set(detail);
          this.projectTypes = projectTypes;

          const savedIds = availability.projectTypeIds;
          const days = availability.days;

          this.pendingProjectTypeIds.set(savedIds);
          this.projectTypeFormControl.patchValue(savedIds, { emitEvent: false });

          if (days.length > 0) {
            const availDays = days.filter((d) => d.isAvailable).map((d) => d.dayOfWeek);
            this.availableDays.set(availDays);
          }

          this.allBranches.set(branches);
          this.preferredCrewOptions.set(preferredCrew);
          this.preferredCrewMembers.set(assignedCrew);
          this.travelStore.loadFromServer(travelAssignments);
          this.crewBonusStore.load(crewBonusPercentages);

          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          console.error('Failed to load foreman configuration', { foremanUserId, err });
          this.isLoading.set(false);
          this.hasError.set(true);
        },
      });
  }

  onSaveChanges(): void {
    const branchId = this.detail()?.homeBranchId;
    if (!branchId || branchId === '00000000-0000-0000-0000-000000000000') {
      this.saveErrorMessage.set('Cannot save: foreman has no home branch assigned.');
      this.saveError.set(true);
      return;
    }

    const travelValidationError = this.travelStore.validate();
    if (travelValidationError) {
      this.saveErrorMessage.set(travelValidationError);
      this.saveError.set(true);
      return;
    }

    this.isSaving.set(true);
    this.saveError.set(false);
    this.saveErrorMessage.set('');

    const payload = {
      projectTypeIds: this.pendingProjectTypeIds(),
      days: this.availableDays().map((day) => ({ dayOfWeek: day })),
    };

    this.calendarService
      .saveDefaultForemanAvailabilityWithPropagation(this.foremanUserId(), branchId, payload)
      .pipe(
        concatMap(() => this.travelStore.saveChanges(this.foremanUserId())),
        concatMap(() =>
          this.calendarService.savePreferredCrew(
            this.foremanUserId(),
            this.preferredCrewMembers().map((m) => m.userId),
          ),
        ),
        concatMap(() =>
          this.crewBonusStore.saveChanges(this.foremanUserId(), this.preferredCrewUserIds()),
        ),
        concatMap(() =>
          this.store
            .loadCrewSchedules(undefined, { suppressErrorState: true })
            .pipe(catchError((err: unknown) => throwError(() => new ReloadAfterSaveError(err)))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.activeModal.close();
        },
        error: (err: unknown) => this.handleSaveError(err),
      });
  }

  onPreferredCrewChange(members: CrewMemberResponse[]): void {
    this.preferredCrewMembers.set(members ?? []);
  }

  isPreferredCrewMemberSelected(userId: string): boolean {
    return this.preferredCrewMembers().some((m) => m.userId === userId);
  }

  removePreferredCrewMember(userId: string): void {
    this.preferredCrewMembers.update((members) => members.filter((m) => m.userId !== userId));
  }

  clearPreferredCrew(): void {
    this.preferredCrewMembers.set([]);
  }

  private handleSaveError(err: unknown): void {
    console.error('Failed to save foreman configuration', { err });
    this.isSaving.set(false);
    if (err instanceof ReloadAfterSaveError) {
      this.saveErrorMessage.set(
        'Your changes were saved, but the calendar could not be refreshed. Please close this dialog and reload the page.',
      );
      this.saveError.set(true);
      return;
    }
    const extracted = extractHttpErrorMessage(err);
    this.saveErrorMessage.set(
      extracted ||
        'Failed to update all configuration. Please verify your changes and try again.',
    );
    this.saveError.set(true);
    this.reloadTravelAssignments();
  }

  toggleDay(dayOfWeek: number): void {
    const current = this.availableDays();
    if (current.includes(dayOfWeek)) {
      this.availableDays.set(current.filter((d) => d !== dayOfWeek));
    } else {
      this.availableDays.set([...current, dayOfWeek]);
    }
  }

  selectAll(): void {
    this.projectTypeFormControl.setValue(this.projectTypes.map((pt) => pt.id));
  }

  deselectAll(): void {
    this.projectTypeFormControl.setValue([]);
  }

  onProjectTypeSearch({ term }: { term: string }): void {
    this.projectTypeSearchTerm.set(term);
  }

  onProjectTypeSearchClear(): void {
    this.projectTypeSearchTerm.set('');
  }

  onProjectTypeDropdownClose(): void {
    this.projectTypeSearchTerm.set('');
  }

  onProjectTypeAdd(): void {
    this.projectTypeSearchTerm.set('');
  }

  closeModal(): void {
    this.activeModal.close();
  }

  private reloadTravelAssignments(): void {
    this.travelStore
      .reloadFromServer(this.foremanUserId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cdr.markForCheck(),
        error: (err: unknown) => {
          console.error('Failed to reload travel assignments after save error', { err });
          this.saveErrorMessage.set(
            (this.saveErrorMessage() || 'Save failed.') +
              ' Travel state could not be refreshed — please reopen the modal.',
          );
          this.cdr.markForCheck();
        },
      });
  }
}
